import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const FUEROS = ['Civil', 'Penal', 'Laboral', 'Familia', 'Contencioso Administrativo', 'Comercial', 'Sucesorio'];

const emptyForm = () => ({
  numero: '', caratula: '', fuero: '', juzgado: '', posicion_procesal: 'actor',
  cliente_ids: [], tipo: 'principal', expediente_principal_id: '', prueba_habilitada: true,
});

export default function ExpedienteNuevoModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [dropdownClienteOpen, setDropdownClienteOpen] = useState(false);
  const [busquedaPrincipal, setBusquedaPrincipal] = useState('');
  const [dropdownPrincipalOpen, setDropdownPrincipalOpen] = useState(false);
  const [principalSeleccionado, setPrincipalSeleccionado] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [cli, exp] = await Promise.all([
        supabase.from('clientes').select('id, nombre, apellido').eq('eliminado', false).order('apellido'),
        supabase.from('expedientes').select('id, numero, caratula').eq('eliminado', false).order('caratula'),
      ]);
      setClientes(cli.data || []);
      setExpedientes(exp.data || []);
    })();
    setForm(emptyForm());
    setBusquedaCliente('');
    setBusquedaPrincipal('');
    setPrincipalSeleccionado(null);
  }, [open]);

  function toggleCliente(id) {
    setForm(f => ({
      ...f,
      cliente_ids: f.cliente_ids.includes(id) ? f.cliente_ids.filter(c => c !== id) : [...f.cliente_ids, id],
    }));
  }

  async function saveExpediente() {
    if (guardando) return;
    if (!form.caratula.trim()) { showToast('La carátula es obligatoria'); return; }
    if (form.tipo === 'incidente' && !form.expediente_principal_id) { showToast('Seleccioná el expediente principal'); return; }
    setGuardando(true);
    const { data: nuevo, error } = await supabase.from('expedientes').insert({
      caratula: form.caratula,
      numero: form.numero || null,
      fuero: (form.fuero && form.fuero !== '__otro__') ? form.fuero : null,
      juzgado: form.juzgado || null,
      posicion_procesal: form.posicion_procesal,
      expediente_principal_id: form.tipo === 'incidente' ? form.expediente_principal_id : null,
      prueba_habilitada: form.prueba_habilitada,
    }).select('id').single();
    if (error || !nuevo) { setGuardando(false); showToast('Error al guardar'); return; }
    if (form.cliente_ids.length > 0) {
      await supabase.from('expedientes_clientes').insert(
        form.cliente_ids.map(cid => ({ expediente_id: nuevo.id, cliente_id: cid }))
      );
    }
    setGuardando(false);
    showToast('Expediente creado ✓');
    onSaved?.();
    onClose();
  }

  function handleClose() { setForm(emptyForm()); onClose(); }

  if (!open) return null;

  const enModoLibre = form.fuero !== '' && !FUEROS.includes(form.fuero);

  return (
    <>
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && handleClose()}>
        <div className="modal" style={{ width: 520 }}>
          <div className="modal-header">
            <div className="modal-title">Nuevo expediente</div>
            <button className="modal-close" onClick={handleClose}>✕</button>
          </div>
          <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="form-grid">
              {/* TIPO */}
              <div className="form-group form-full">
                <label className="form-label">Tipo</label>
                <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', width: 'fit-content' }}>
                  {['principal', 'incidente'].map(t => (
                    <button key={t} type="button" onClick={() => { setForm(f => ({ ...f, tipo: t, expediente_principal_id: '' })); setPrincipalSeleccionado(null); setBusquedaPrincipal(''); }}
                      style={{ padding: '6px 20px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: form.tipo === t ? 'var(--blue)' : 'var(--surface2)', color: form.tipo === t ? '#fff' : 'var(--text3)' }}>
                      {t === 'principal' ? 'Principal' : 'Incidente'}
                    </button>
                  ))}
                </div>
              </div>

              {/* EXPEDIENTE PRINCIPAL si es incidente */}
              {form.tipo === 'incidente' && (
                <div className="form-group form-full">
                  <label className="form-label">Expediente principal *</label>
                  {principalSeleccionado ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: '#f3e8ff', border: '1px solid #c4b5fd' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#5b21b6' }}>{principalSeleccionado.caratula}</div>
                        {principalSeleccionado.numero && <div style={{ fontSize: 11, color: '#7c3aed' }}>{principalSeleccionado.numero}</div>}
                      </div>
                      <span onClick={() => { setPrincipalSeleccionado(null); setForm(f => ({ ...f, expediente_principal_id: '' })); }}
                        style={{ cursor: 'pointer', color: '#7c3aed', fontWeight: 700, fontSize: 16 }}>×</span>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <input className="form-input" placeholder="Buscar por carátula o número..." value={busquedaPrincipal}
                        onChange={e => setBusquedaPrincipal(e.target.value)}
                        onFocus={() => setDropdownPrincipalOpen(true)}
                        onBlur={() => setTimeout(() => setDropdownPrincipalOpen(false), 150)}
                        autoComplete="off" />
                      {dropdownPrincipalOpen && (
                        <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 200, maxHeight: 200, overflowY: 'auto', marginBottom: 4, boxShadow: '0 -4px 16px rgba(0,0,0,0.3)' }}>
                          {expedientes.filter(e => { const t = busquedaPrincipal.toLowerCase(); return !t || e.caratula?.toLowerCase().includes(t) || e.numero?.toLowerCase().includes(t); }).map(e => (
                            <div key={e.id} onMouseDown={() => { setPrincipalSeleccionado(e); setForm(f => ({ ...f, expediente_principal_id: e.id })); setBusquedaPrincipal(''); }}
                              style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                              <div style={{ fontWeight: 500 }}>{e.caratula}</div>
                              {e.numero && <div style={{ fontSize: 11, color: 'var(--text4)' }}>{e.numero}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group form-full">
                <label className="form-label">Carátula *</label>
                <input className="form-input" autoFocus value={form.caratula} onChange={e => setForm(f => ({ ...f, caratula: e.target.value }))} placeholder="Ej: García c/ López s/ Daños y Perjuicios" />
              </div>
              <div className="form-group">
                <label className="form-label">N° Expediente</label>
                <input className="form-input" value={form.numero} onChange={e => setForm(f => ({ ...f, numero: e.target.value }))} placeholder="Ej: 12345/2024" />
              </div>
              <div className="form-group">
                <label className="form-label">Posición procesal</label>
                <select className="form-select" value={form.posicion_procesal} onChange={e => setForm(f => ({ ...f, posicion_procesal: e.target.value }))}>
                  <option value="actor">Actor</option>
                  <option value="demandado">Demandado</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fuero</label>
                {!enModoLibre ? (
                  <select className="form-select" value={form.fuero} onChange={e => setForm(f => ({ ...f, fuero: e.target.value }))}>
                    <option value="">Seleccioná</option>
                    {FUEROS.map(f => <option key={f}>{f}</option>)}
                    <option value="__otro__">Otro...</option>
                  </select>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="form-input" autoFocus value={form.fuero === '__otro__' ? '' : form.fuero}
                      onChange={e => setForm(f => ({ ...f, fuero: e.target.value || '__otro__' }))} placeholder="Especificá el fuero" />
                    <button className="btn btn-ghost btn-sm" onClick={() => setForm(f => ({ ...f, fuero: '' }))}>✕</button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Juzgado / Tribunal</label>
                <input className="form-input" value={form.juzgado} onChange={e => setForm(f => ({ ...f, juzgado: e.target.value }))} placeholder="Ej: Juzgado Civil N° 5" />
              </div>
              <div className="form-group form-full">
                <label className="form-label">¿Tiene etapa de prueba?</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{ v: true, label: 'Sí' }, { v: false, label: 'No' }].map(({ v, label }) => (
                    <button key={String(v)} type="button" className={`btn btn-sm ${form.prueba_habilitada === v ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setForm(f => ({ ...f, prueba_habilitada: v }))}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group form-full">
                <label className="form-label">Clientes vinculados</label>
                {form.cliente_ids.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {form.cliente_ids.map(id => {
                      const c = clientes.find(x => x.id === id);
                      return c ? (
                        <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'var(--blue-light)', border: '1px solid var(--blue)', color: 'var(--blue)', fontSize: 12 }}>
                          {c.nombre} {c.apellido}
                          <span onClick={() => toggleCliente(id)} style={{ cursor: 'pointer', fontWeight: 700, marginLeft: 2 }}>×</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
                <div style={{ position: 'relative' }}>
                  <input className="form-input" placeholder="Buscar cliente por nombre o DNI..." value={busquedaCliente}
                    onChange={e => setBusquedaCliente(e.target.value)}
                    onFocus={() => setDropdownClienteOpen(true)}
                    onBlur={() => setTimeout(() => setDropdownClienteOpen(false), 150)}
                    autoComplete="off" />
                  {dropdownClienteOpen && (
                    <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 200, maxHeight: 200, overflowY: 'auto', marginBottom: 4, boxShadow: '0 -4px 16px rgba(0,0,0,0.3)' }}>
                      {clientes.filter(c => { const t = busquedaCliente.toLowerCase(); return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t) || (c.dni || '').includes(t); }).map(c => (
                        <div key={c.id} onMouseDown={() => { toggleCliente(c.id); setBusquedaCliente(''); }}
                          style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: form.cliente_ids.includes(c.id) ? 'var(--blue-light)' : 'transparent', color: form.cliente_ids.includes(c.id) ? 'var(--blue)' : 'var(--text2)' }}>
                          <span>{c.nombre} {c.apellido}</span>
                          {form.cliente_ids.includes(c.id) && <span>✓</span>}
                        </div>
                      ))}
                      {clientes.filter(c => { const t = busquedaCliente.toLowerCase(); return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t); }).length === 0 && (
                        <div style={{ padding: '10px 14px', color: 'var(--text4)', fontSize: 13 }}>Sin resultados</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 10, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 7 }}>
              💡 Podés completar juez, partes, fechas y demás datos desde la ficha del expediente.
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={handleClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={saveExpediente} disabled={guardando}>{guardando ? 'Guardando...' : 'Crear expediente'}</button>
          </div>
        </div>
      </div>
      {toast && <div className="toast" style={{ zIndex: 1200 }}>✓ {toast}</div>}
    </>
  );
}
